import '@/components/mention/mention.scss';

import Quill, { RangeStatic } from 'quill';
import Vue, { PropType, VNode } from 'vue';
import { SelectItemKey } from 'vuetify';

import { ExtractVue } from '@/utils/mixins';
import { IUser, User } from '@/models';
import { defaultFilter, getPropertyFromItem } from '@/utils/helpers';

import Delta from 'quill-delta';

import MentionMenuListVue from '@/components/mention/MentionMenuList.vue';

interface IMentionChar {
  mentionChar: string;
  mentionCharIndex: number;
}

/**
 * ExtractVue Returns the instance type from a VueConstructor
 * Useful for adding types when using mixins().extend()
 */
interface Options extends ExtractVue<typeof Vue> {
  quill: Quill | null;
  isFocused: boolean;
  $refs: {
    editor: HTMLElement;
    menu: Vue & {
      activeTile?: HTMLElement;
      listIndex: number;
      changeListIndex(e: KeyboardEvent): void;
    };
  };
}

export default Vue.extend<Options>({ functional: false }).extend({
  props: {
    /** Mentions Module flag */
    mentions: { type: Boolean, default: false },

    itemDisabled: {
      type: [String, Array, Function] as PropType<SelectItemKey>,
      default: 'disabled'
    },
    mentionItemText: {
      type: [String, Array, Function] as PropType<SelectItemKey>,
      default: 'text'
    },

    noDataText: {
      type: String,
      default: 'No Mentions Found'
    },

    mentionableUsers: {
      type: Array as PropType<IUser[]>,
      default: () => []
    },

    mentionLoading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isMenuActive: false,
      caretPosition: 0,
      mentionDenotationChars: ['@'],
      mentionCharPosition: null as null | number,
      minChars: 0,
      maxChars: 31,
      internalSearch: '',
      allowedChars: /^[a-zA-Z0-9_]*$/ as RegExp | ((...args: any) => any),

      menuPositionX: -1,
      menuPositionY: -1,
      offsetTop: 2,
      offsetLeft: 0,

      menuMaxHeight: 304
    };
  },

  computed: {
    filteredMentionableUsers(): IUser[] {
      return this.mentionableUsers.filter(user => {
        const value = getPropertyFromItem(user, this.mentionItemText);
        const text = value != null ? String(value) : '';

        return defaultFilter(text, String(this.internalSearch), user);
      });
    }
  },

  created() {
    if (this.mentions) {
      let mentionableUsers = [...this.mentionableUsers];
      // start a watcher to update the mention users list wheneven it updates from the components
      this.$watch(
        'mentionableUsers',
        users => {
          // make this list reference reactive into the MentionBlot class
          mentionableUsers = users;
        },
        { deep: true, immediate: true }
      );

      //  blot refers to a fundamental unit of content or data within the quill editor.
      const Embed = Quill.import('blots/embed');

      //  To extends the functionality of the embed blot to allow its usage of mentions in the quill editor
      //  have have to extends the class and register it as a format.
      // Its isn the created as the mentioned users list need to be reactive for changes to that attribute
      class MentionBlot extends Embed {
        static create(value: IUser | string) {
          const node: Element = super.create(value);
          node.setAttribute('contenteditable', 'false');

          // in case of copying mentions the mention will be a string
          if (typeof value == 'string') {
            const mentionedUser = mentionableUsers.find(
              user => user.id == Number(value)
            );
            if (mentionedUser) {
              const mention = new User(mentionedUser);
              node.setAttribute('data-mention', value);
              node.textContent = `@${mention.toString()}`;
            }
          } else {
            node.setAttribute('data-mention', String(value.id));
            node.textContent = `@${value.toString()}`;
          }
          return node;
        }

        static value(domNode: Element) {
          return domNode.getAttribute('data-mention');
        }

        static blotName = 'mention';
        static className = 'mention';
        static tagName = 'SPAN';
        static mention = true;
      }

      Quill.register({ 'formats/mention': MentionBlot }, true);
    }
  },

  methods: {
    mentionTextHandler() {
      const range = this.quill!.getSelection();

      if (range == null) return;

      this.caretPosition = range.index;

      // get text before cursor to run mention denotation check on
      const textBeforeCursor = this.getTextBeforeCursor();

      const { mentionChar, mentionCharIndex } = this.getMentionCharIndex(
        textBeforeCursor,
        this.mentionDenotationChars
      );

      // if mention denotation text is valid then follow through with mention menu process
      if (
        // clean up also check if there is no space before
        this.hasValidMentionCharIndex(mentionCharIndex, textBeforeCursor)
      ) {
        // TODO: LH - 2023-10-23 - Set the mention char position for Positioning mention Menu card
        this.mentionCharPosition =
          this.caretPosition - (textBeforeCursor.length - mentionCharIndex);

        const textAfter = textBeforeCursor.substring(
          mentionCharIndex + (mentionChar?.length ?? 0)
        );

        // use for internal search of mention menu list
        this.internalSearch = textAfter;

        if (
          textAfter.length >= this.minChars &&
          this.hasValidChars(textAfter, this.getAllowedCharsRegex(mentionChar))
        ) {
          // Once mentioned character is found open mention menu
          this.openMentionMenu();
          this.$nextTick(this.setMenuMentionPosition);
        } else {
          this.closeMentionMenu();
        }
      } else {
        this.internalSearch = '';
        this.closeMentionMenu();
      }
    },

    getTextBeforeCursor() {
      const startPos = Math.max(0, this.caretPosition - this.maxChars);
      const textBeforeCursorPos =
        this.quill!.getText(startPos, this.caretPosition - startPos) ?? '';
      return textBeforeCursorPos;
    },

    getMentionCharIndex(
      text: string,
      mentionDenotationChars: string[]
    ): IMentionChar {
      return mentionDenotationChars.reduce(
        (prev: IMentionChar, mentionChar) => {
          const mentionCharIndex = text.lastIndexOf(mentionChar);

          if (mentionCharIndex > prev.mentionCharIndex) {
            return {
              mentionChar,
              mentionCharIndex
            };
          }
          return {
            mentionChar: prev.mentionChar,
            mentionCharIndex: prev.mentionCharIndex
          };
        },
        { mentionChar: '', mentionCharIndex: -1 }
      );
    },

    hasValidChars(text: string, allowedChars: RegExp) {
      return allowedChars.test(text);
    },

    hasValidMentionCharIndex(mentionCharIndex: number, text: string) {
      let isValidCharIndex = false;
      if (mentionCharIndex > -1) {
        if (
          mentionCharIndex === 0 ||
          !!text[mentionCharIndex - 1].match(/\s/g)
        ) {
          isValidCharIndex = true;
        }
      }
      return isValidCharIndex;
    },

    getAllowedCharsRegex(denotationChar: string) {
      if (this.allowedChars instanceof RegExp) {
        return this.allowedChars;
      } else {
        return this.allowedChars(denotationChar);
      }
    },

    setMenuMentionPosition() {
      if (this.$el && this.quill) {
        const quillContainerRect = this.$refs.editor.getBoundingClientRect();
        const mentionCharaterPosition = this.quill.getBounds(
          this.mentionCharPosition!
        );

        let topPos = this.offsetTop;
        let leftPos = this.offsetLeft;

        // handle horizontal positioning
        leftPos += mentionCharaterPosition.left;

        const containerWidth =
          (this.$refs.menu.$el as HTMLDivElement).offsetWidth + this.offsetLeft;
        if (this.containerRightIsNotVisible(leftPos, quillContainerRect)) {
          const quillWidth = quillContainerRect.width;
          leftPos = quillWidth - containerWidth;
        }

        topPos += quillContainerRect.top;

        // default to the top if the bottom is not visible
        if (this.containerBottomIsNotVisible(topPos, quillContainerRect)) {
          const overMentionCharPos = this.offsetTop * -1;

          topPos = overMentionCharPos - this.menuMaxHeight;
        }

        topPos += mentionCharaterPosition.bottom;

        this.menuPositionX = this.$el.getBoundingClientRect().x + leftPos + 24; //24 is natual padding times 2
        this.menuPositionY = topPos;
      }
    },

    containerBottomIsNotVisible(topPos: number, containerPos: DOMRect) {
      const mentionContainerBottom =
        topPos +
        (this.$refs.menu.$el as HTMLDivElement).offsetHeight +
        containerPos.top;
      return mentionContainerBottom > window.pageYOffset + window.innerHeight;
    },

    containerRightIsNotVisible(leftPos: number, containerPos: DOMRect) {
      const rightPos =
        leftPos +
        (this.$refs.menu.$el as HTMLDivElement).offsetWidth +
        containerPos.left;
      const browserWidth =
        window.pageXOffset + document.documentElement.clientWidth;
      return rightPos > browserWidth;
    },

    openMentionMenu() {
      this.isMenuActive = true;
      this.$emit('openMentionMenu');
    },

    closeMentionMenu() {
      this.isMenuActive = false;
    },

    onSelectMentionItem(user: IUser) {
      const editor = this.quill!.getSelection(true)!;
      this.caretPosition = editor.index;

      const insertAtMentionCharPosition = this.mentionCharPosition!;

      if (editor == null) return;

      this.quill!.deleteText(
        this.mentionCharPosition!,
        editor.index - this.mentionCharPosition!
      );

      this.insertMentionDisplay(insertAtMentionCharPosition, user);

      // add space after inserting the text
      this.quill!.insertText(insertAtMentionCharPosition + 1, ' ', 'user');

      // setSelection here sets cursor position and adds an extra space
      this.quill!.setSelection(
        insertAtMentionCharPosition + 2,
        user.toString().length,
        'user'
      );

      this.closeMentionMenu();
    },

    /** Insert the mention embedded blot in position index */
    insertMentionDisplay(positionIndex: number, mention: IUser) {
      this.quill!.insertEmbed(
        positionIndex,
        'mention',
        mention,
        'user' // emit update from user api
      );
    },

    // COMPONENTS
    /**
     * Add mentions menu to display avaliable mention users.
     */
    genMentionsMenu(): VNode {
      return this.$createElement(
        'VMenu',
        {
          attrs: { role: undefined },
          props: {
            activator: this.$refs.editor,
            value: this.isMenuActive,
            closeOnClick: false,
            closeOnContentClick: false,
            disableKeys: true,
            openOnClick: false,
            maxWidth: 250,
            maxHeight: this.menuMaxHeight,
            offsetY: true,
            nudgeBottom: 1, // one pixel lower to show content

            positionX: this.menuPositionX,
            positionY: this.menuPositionY,
            absolute: true
          },
          on: {
            input: (val: boolean) => {
              this.isMenuActive = val;
              this.isFocused = val;
            }
          },
          ref: 'menu'
        },
        [
          this.$createElement(
            'VCard',
            {
              staticClass: 'd-flex flex-column'
            },
            [
              this.$createElement(MentionMenuListVue, {
                props: {
                  mentionableUsers: this.filteredMentionableUsers,
                  itemDisabled: this.itemDisabled,
                  mentionItemText: this.mentionItemText,
                  searchInput: this.internalSearch,
                  noDataText: this.noDataText,
                  loading: this.mentionLoading
                },
                on: {
                  keydown: this.onKeyDown,
                  select: this.onSelectMentionItem
                },
                scopedSlots: {
                  item: this.$scopedSlots.item
                }
              })
            ]
          )
        ]
      );
    },

    keyboardBindings() {
      // This will overwrite the default binding also named 'tab' and 'enter'
      return {
        tab: {
          key: 'Tab',
          handler: (range: RangeStatic, context: any) => {
            if (this.isMenuActive) {
              return this.onTabDown(context.event);
            } else {
              // default tab keydown functionality
              if (context.format.table) return true;
              const delta = new Delta()
                .retain(range.index)
                .delete(range.length)
                .insert('\t');
              // type set to as any as the quill-Delta type not same as Delta
              this.quill!.updateContents(delta as any, 'user');
              this.quill!.setSelection(
                { index: range.index + 1, length: range.length },
                'silent'
              );
              return false;
            }
          }
        },
        enter: {
          key: 'Enter',
          shiftKey: null,
          handler: (range: RangeStatic, context: any) => {
            const menu = this.$refs.menu;
            const activeTile = menu.activeTile;
            if (activeTile && this.isMenuActive) {
              this.$nextTick(() => {
                activeTile.click();
              });
            } else {
              // default enter keydown functionality
              (this.quill!.keyboard as any).handleEnter(range, context);
            }
          }
        }
      };
    },

    onKeyDown(e: KeyboardEvent) {
      const key = e.key;
      const menu = this.$refs.menu;

      this.$emit('keydown', e);

      if (!menu) return;

      // If menu is active, allow default
      // listIndex change from menu
      if (
        this.isMenuActive &&
        ['ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter'].includes(key)
      ) {
        this.$nextTick(() => {
          menu.changeListIndex(e);
          this.$emit('update:list-index', menu.listIndex);
        });
      }

      // If escape deactivate the menu
      if (key === 'Escape') return this.onEscDown(e);

      // If space, arrowleft, arrowright preventDefault
      if (this.isMenuActive && ['ArrowLeft', 'ArrowRight', ' '].includes(key))
        return this.onPreventKeyDown(e);
    },

    onEscDown(e: Event) {
      e.preventDefault();
      if (this.isMenuActive) {
        e.stopPropagation();
        this.isMenuActive = false;
      }
    },

    onTabDown(e: KeyboardEvent) {
      const menu = this.$refs.menu;
      const activeTile = menu.activeTile;

      // An item that is selected by
      // menu-index should toggled
      if (activeTile) {
        e.preventDefault();
        e.stopPropagation();

        this.$nextTick(() => {
          activeTile.click();
        });
      } else {
        // If we make it here,
        // the user has no selected indexes
        // and is probably tabbing out
        this.closeMentionMenu();
      }
    },

    onPreventKeyDown(e: KeyboardEvent) {
      e.preventDefault();
    }
  }
});
